import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import { useMaterialUIController } from "context";
import useSalesData from "hooks/useSalesData";
import CustomPagination from "examples/CustomPagination/CustomPagination";
import salesDataTableData from "layouts/salesData/data/salesDataTableData";
import MDButton from "components/MDButton";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import theme from "assets/theme";
import { debounce } from "lodash";
import {
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  useMediaQuery,
  IconButton,
  Collapse,
} from "@mui/material";
import useDashboard from "hooks/useDashboard";
function Tables() {
  const { getsalesDataList, salesDataList, salesDataPreview, salesDataSync } = useSalesData();
  const { columns, rows } = salesDataTableData();
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const fullScreen = useMediaQuery(theme.breakpoints.down("lg"));

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPage, setTotalPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState("");
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const { searchTerm } = useDashboard();

  const [openRows, setOpenRows] = useState({});

  const handleInputChange = (index, field, value) => {
    const updatedData = [...previewData];
    updatedData[index][field] = value;
    setPreviewData((prevData) => {
      return prevData.map((item, i) =>
        i === index
          ? {
              ...item,
              [field]: value,
              ...(field === "distributor" && { distributorExist: true }),
              ...(field === "gstNo" && { gstNoExist: true }),
              ...(field === "invoice" && { invoiceExist: false }),
              ...(field === "salesrep" && { salesRepExist: true }),
              ...(field === "territory" && { territoryExist: true }),
            }
          : item
      );
    });
  };

  const handleProductChange = (index, productIndex, field, value) => {
    setPreviewData((prevData) =>
      prevData.map((item, i) =>
        i === index
          ? {
              ...item,
              products: item.products.map((product, pIndex) =>
                pIndex === productIndex
                  ? {
                      ...product,
                      [field]: value,
                      ...(field === "category" && { categoryExist: true }),
                      ...(field === "product" && { productExist: true }),
                    }
                  : product
              ),
            }
          : item
      )
    );
  };

  const handleCheckboxChange = (index) => {
    const updatedData = [...previewData];
    updatedData[index].selected = !updatedData[index].selected;
    setPreviewData(updatedData);
  };

  const handleSelectAll = () => {
    const allSelected = previewData.every((item) => item.selected);
    const updatedData = previewData.map((item) => ({
      ...item,
      selected: !allSelected,
    }));
    setPreviewData(updatedData);
  };

  const toggleRow = (index) => {
    setOpenRows((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  // Function to fetch dealers
  const fetchSalesData = () => {
    const query = searchTerm.length >= 3 ? searchTerm : ""; // Apply search only if length >= 3
    getsalesDataList({ limit, page, search: query });
  };

  // Debounced search function
  const debouncedfetchSalesData = debounce(fetchSalesData, 500);

  // Initial Load (Mandatory)
  useEffect(() => {
    fetchSalesData(); // Always fetch initial data on mount
  }, []);

  // Search & Pagination Updates
  useEffect(() => {
    if (searchTerm.length === 0 || searchTerm.length >= 3) {
      debouncedfetchSalesData();
    }
    return () => debouncedfetchSalesData.cancel(); // Cleanup debounce
  }, [searchTerm, page, limit]);

  useEffect(() => {
    if (salesDataList.page && salesDataList.limit) {
      setPage(salesDataList.page);
      setLimit(salesDataList.limit);
      setTotalPage(salesDataList.pages || 1);
      setTotal(salesDataList.total || 0);
    }
  }, [salesDataList]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    getsalesDataList({ limit: 10, page: newPage });
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setSelectedFile(null);
    setError("");
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedFile(null);
    setError("");
  };

  const handleClosePreviewDialog = () => {
    setOpenPreviewDialog(false);
    setPreviewData([]);
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];

    if (file) {
      const validMimeTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];
      const validExtensions = [".xlsx", ".xls"];

      const fileExtension = file.name.slice(((file.name.lastIndexOf(".") - 1) >>> 0) + 2);

      if (!validMimeTypes.includes(file.type) || !validExtensions.includes(`.${fileExtension}`)) {
        setError("Invalid file type. Please upload an Excel file (.xls, .xlsx).");
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setError("");
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ".xls,.xlsx",
    multiple: false,
  });

  const handleConfirm = async () => {
    const salesData = previewData
      .filter((item) => item.selected)
      .map(({ selected, id, ...rest }) => rest); // Remove 'selected' key

    if (salesData.length === 0) {
      alert("Please select at least one record before submitting.");
      return;
    }

    const payload = { sales: salesData };

    try {
      const res = await salesDataSync(payload);
      if (res.success) {
        alert("SalesData synced successfully");
        handleClosePreviewDialog();
        fetchSalesData();
      } else {
        alert(res.message || "Something went wrong!");
        setPreviewData(
          res?.error?.sales.map((item) => {
            const hasMissingInvoice = !item?.invoiceExist;
            const hasMissingProductOrCategory = item.products.some(
              (product) => !product.productExist || !product.categoryExist
            );

            return {
              ...item,
              selected:
                item?.distributorExist &&
                item?.gstNoExist &&
                hasMissingInvoice &&
                hasMissingProductOrCategory,
            };
          }) || []
        );
      }
    } catch (error) {
      console.error("SalesData sync error:", error);
      alert("An error occurred while syncing salesData");
    }
  };

  const handleFileUpdate = () => {
    if (selectedFile) {
      const formData = new FormData();
      formData.append("file", selectedFile);

      salesDataPreview(formData)
        .then((response) => {
          if (response.success) {
            setPreviewData(
              response?.data?.preview?.map((item) => ({
                ...item,
                selected: true,
              }))
            );
            setOpenPreviewDialog(true);
          } else {
            window.alert(response.message || "Something went wrong!");
          }
          handleCloseDialog();
        })
        .catch((error) => {
          console.error("Error uploading file:", error);
          window.alert("Failed to upload and sync file. Please try again.");
        });

      setOpenDialog(false);
    } else {
      window.alert("Please select a file before updating.");
    }
  };
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                bgColor={sidenavColor}
                borderRadius="lg"
                coloredShadow={sidenavColor}
              >
                <MDTypography variant="h6" color="white">
                  SalesData Table
                </MDTypography>
                <MDButton variant="contained" onClick={handleOpenDialog}>
                  Update & Sync File
                </MDButton>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            </Card>
            <MDBox
              sx={{ width: "100%", display: "flex", justifyContent: "start", marginTop: "1rem" }}
            >
              <CustomPagination
                totalCount={totalPage}
                currentPage={page}
                onPageChange={handlePageChange}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>

      {/* Drag & Drop File Upload Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>File Update & Sync</DialogTitle>
        <DialogContent>
          <MDBox
            {...getRootProps()}
            sx={{
              border: "2px dashed #999",
              borderRadius: "8px",
              padding: "20px",
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: isDragActive ? "#f0f0f0" : "#fff",
            }}
          >
            <input {...getInputProps()} />
            <CloudUploadIcon sx={{ fontSize: 150, color: "#999" }} />
            <MDTypography sx={{ mt: 2 }}>
              {isDragActive
                ? "Drop the file here..."
                : "Drag & drop an Excel file here, or click to browse"}
            </MDTypography>
          </MDBox>
          {selectedFile && (
            <MDTypography sx={{ mt: 2, color: "green" }}>
              Selected file: {selectedFile.name}
            </MDTypography>
          )}
          {error && (
            <MDTypography color="error" sx={{ mt: 1 }}>
              {error}
            </MDTypography>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseDialog} color="dark">
            Cancel
          </MDButton>
          <MDButton onClick={handleFileUpdate} color="info" disabled={!selectedFile}>
            Update & Sync
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openPreviewDialog}
        onClose={handleClosePreviewDialog}
        fullScreen={fullScreen}
        aria-labelledby="responsive-dialog-title"
        fullWidth
        PaperProps={{
          style: { width: "75vw", maxWidth: "none" },
        }}
      >
        <DialogTitle>SalesData Sync Preview</DialogTitle>
        <DialogContent>
          <TableContainer
            component={Paper}
            sx={{ maxHeight: 500, overflowY: "auto", borderRadius: 2 }}
          >
            `{" "}
            <Table>
              {/* <TableHead> */}
              <TableRow>
                <TableCell>
                  <Checkbox
                    indeterminate={
                      previewData?.some((item) => item.selected) &&
                      !previewData?.every((item) => item.selected)
                    }
                    checked={previewData.every((item) => item.selected)}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell />
                <TableCell>Date</TableCell>
                <TableCell>Dealer</TableCell>
                <TableCell>GST No</TableCell>
                <TableCell>Distributor</TableCell>
                <TableCell>Invoice</TableCell>
                <TableCell>Sales Rep</TableCell>
                <TableCell>Territory</TableCell>
                <TableCell>Town</TableCell>
              </TableRow>
              {/* </TableHead> */}
              <TableBody>
                {previewData.map((row, index) => {
                  const distributorExist = row.distributorExist === false;
                  const gstNoExist = row.gstNoExist === false;
                  const invoiceExist = row.invoiceExist === true;
                  const salesRepExist = row.salesRepExist === false;
                  const territoryExist = row.territoryExist === false;
                  const isWrong =
                    invoiceExist ||
                    gstNoExist ||
                    distributorExist ||
                    territoryExist ||
                    salesRepExist;

                  return (
                    <>
                      <TableRow
                        key={index}
                        hover
                        sx={{ backgroundColor: isWrong ? "#ffebee" : "inherit" }}
                      >
                        <TableCell>
                          <Checkbox
                            checked={row.selected}
                            onChange={() => handleCheckboxChange(index)}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton onClick={() => toggleRow(index)}>
                            {false ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            value={row.date}
                            onChange={(e) => handleInputChange(index, "date", e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            value={row.dealer}
                            onChange={(e) => handleInputChange(index, "dealer", e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            value={row.gstNo}
                            onChange={(e) => handleInputChange(index, "gstNo", e.target.value)}
                            sx={{
                              border: isWrong && gstNoExist && "2px solid red",
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            value={row.distributor}
                            onChange={(e) =>
                              handleInputChange(index, "distributor", e.target.value)
                            }
                            sx={{
                              border: isWrong && distributorExist && "2px solid red",
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            value={row.invoice}
                            onChange={(e) => handleInputChange(index, "invoice", e.target.value)}
                            sx={{
                              border: isWrong && invoiceExist && "2px solid red",
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            value={row.salesrep}
                            onChange={(e) => handleInputChange(index, "salesrep", e.target.value)}
                            sx={{
                              border: isWrong && salesRepExist && "2px solid red",
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            value={row.territory}
                            onChange={(e) => handleInputChange(index, "territory", e.target.value)}
                            sx={{
                              border: isWrong && territoryExist && "2px solid red",
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            value={row.town}
                            onChange={(e) => handleInputChange(index, "town", e.target.value)}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={9} sx={{ padding: 0 }}>
                          <Collapse in={true} timeout="auto" unmountOnExit>
                            <Table size="small" sx={{ marginLeft: 5, marginBottom: 2 }}>
                              {/* <TableHead> */}
                              <TableRow>
                                <TableCell>Product</TableCell>
                                <TableCell>Quantity</TableCell>
                                <TableCell>Category</TableCell>
                                <TableCell>Purchase Rate</TableCell>
                                <TableCell>Billing Rate</TableCell>
                                <TableCell>Purchase Amount</TableCell>
                                <TableCell>Actual Rate</TableCell>
                              </TableRow>
                              {/* </TableHead> */}
                              <TableBody>
                                {row.products.map((product, productIndex) => {
                                  const categoryExist = product.categoryExist === false;
                                  const productExist = product.productExist === false;
                                  const isWrong = productExist || categoryExist;
                                  return (
                                    <TableRow
                                      key={productIndex}
                                      hover
                                      sx={{ backgroundColor: isWrong ? "#ffebee" : "inherit" }}
                                    >
                                      <TableCell>
                                        <TextField
                                          fullWidth
                                          variant="outlined"
                                          size="small"
                                          value={product.product}
                                          onChange={(e) =>
                                            handleProductChange(
                                              index,
                                              productIndex,
                                              "product",
                                              e.target.value
                                            )
                                          }
                                          sx={{
                                            border: isWrong && productExist && "2px solid red",
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <TextField
                                          type="number"
                                          fullWidth
                                          variant="outlined"
                                          size="small"
                                          value={product.quantity}
                                          onChange={(e) =>
                                            handleProductChange(
                                              index,
                                              productIndex,
                                              "quantity",
                                              e.target.value
                                            )
                                          }
                                          inputProps={{ min: 0 }}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <TextField
                                          fullWidth
                                          variant="outlined"
                                          size="small"
                                          value={product.category}
                                          onChange={(e) =>
                                            handleProductChange(
                                              index,
                                              productIndex,
                                              "category",
                                              e.target.value
                                            )
                                          }
                                          sx={{
                                            border: isWrong && categoryExist && "2px solid red",
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <TextField
                                          type="number"
                                          fullWidth
                                          variant="outlined"
                                          size="small"
                                          value={product.purchaseRate}
                                          onChange={(e) =>
                                            handleProductChange(
                                              index,
                                              productIndex,
                                              "purchaseRate",
                                              e.target.value
                                            )
                                          }
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <TextField
                                          type="number"
                                          fullWidth
                                          variant="outlined"
                                          size="small"
                                          value={product.billingRate}
                                          onChange={(e) =>
                                            handleProductChange(
                                              index,
                                              productIndex,
                                              "billingRate",
                                              e.target.value
                                            )
                                          }
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <TextField
                                          type="number"
                                          fullWidth
                                          variant="outlined"
                                          size="small"
                                          value={product.purchaseAmount}
                                          onChange={(e) =>
                                            handleProductChange(
                                              index,
                                              productIndex,
                                              "purchaseAmount",
                                              e.target.value
                                            )
                                          }
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <TextField
                                          type="number"
                                          fullWidth
                                          variant="outlined"
                                          size="small"
                                          value={product.actualRate}
                                          onChange={(e) =>
                                            handleProductChange(
                                              index,
                                              productIndex,
                                              "actualRate",
                                              e.target.value
                                            )
                                          }
                                        />
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </>
                  );
                })}
              </TableBody>
            </Table>
            `
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleClosePreviewDialog} color="dark">
            Cancel
          </MDButton>
          <MDButton onClick={handleConfirm} color="info">
            Confirm
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Tables;
